/**
 * ProductEditorModal
 *
 * Modal for creating and editing products.
 * Supports scanning product URLs to auto-fill fields.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    ShoppingBag,
    Link,
    Search,
    Loader2,
    Image,
    DollarSign,
    Tag,
    FileText,
    Plus,
    CheckCircle,
    AlertCircle,
    Sparkles,
    Wand2,
    Upload,
    Maximize2,
    Crop,
    Paintbrush,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { scanProductUrl, generateVisualNotes, deduplicateImageObjects, DEFAULT_PRODUCT } from '../../services/productService';
import ImageCropCanvas from '../ImageCropCanvas';
import MaskDrawingCanvas from '../MaskDrawingCanvas';
import ConfirmationModal from '../ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

const ProductEditorModal = ({
    isOpen,
    onClose,
    onSave,
    product = null,
    loading = false
}) => {
    const [form, setForm] = useState({ ...DEFAULT_PRODUCT });
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [urlInput, setUrlInput] = useState('');
    const [newFeature, setNewFeature] = useState('');
    const [newImageUrl, setNewImageUrl] = useState('');
    const [generatingNotes, setGeneratingNotes] = useState(false);
    const fileInputRef = useRef(null);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [fullscreenIndex, setFullscreenIndex] = useState(null);
    const [showCropCanvas, setShowCropCanvas] = useState(false);
    const [cropImageIndex, setCropImageIndex] = useState(null);
    const [showMaskDrawer, setShowMaskDrawer] = useState(false);
    const [maskImageIndex, setMaskImageIndex] = useState(null);

    const { isDirty, captureInitialState, showUnsavedModal, setShowUnsavedModal, handleCloseAttempt } = useUnsavedChanges(form);

    // Initialize form when product changes
    useEffect(() => {
        let initialData;
        if (product) {
            // Merge imageUrl into images array if it exists and isn't already there
            let images = product.images || [];
            if (product.imageUrl) {
                const alreadyInImages = images.some(img => {
                    const url = typeof img === 'string' ? img : img.url;
                    return url === product.imageUrl;
                });
                if (!alreadyInImages) {
                    images = [{ url: product.imageUrl, alt: 'Primary product image' }, ...images];
                }
            }
            // Remove duplicate images by normalized filename and size
            images = deduplicateImageObjects(images);
            initialData = { ...DEFAULT_PRODUCT, ...product, images };
            setForm(initialData);
            setUrlInput(product.productUrl || '');
        } else {
            initialData = { ...DEFAULT_PRODUCT, images: [] };
            setForm(initialData);
            setUrlInput('');
        }
        captureInitialState(initialData);
        setScanError(null);
        setNewImageUrl('');
        setFullscreenIndex(null);
    }, [product, isOpen]);

    const getImageUrl = (img) => typeof img === 'string' ? img : img.url;

    const handleAttemptClose = () => {
        if (!handleCloseAttempt()) {
            onClose();
        }
    };

    const handleSaveAndClose = () => {
        const firstUrl = form.images?.[0] ? getImageUrl(form.images[0]) : '';
        onSave({ ...form, imageUrl: firstUrl });
        setShowUnsavedModal(false);
    };

    const handleDiscardAndClose = () => {
        setShowUnsavedModal(false);
        onClose();
    };

    const handleScanUrl = async () => {
        if (!urlInput.trim()) return;

        setScanning(true);
        setScanError(null);

        try {
            const scannedData = await scanProductUrl(urlInput.trim());
            // Merge imageUrl into images array so there's one unified list
            let mergedImages = scannedData.images || [];
            if (scannedData.imageUrl) {
                const alreadyInImages = mergedImages.some(img => {
                    const url = typeof img === 'string' ? img : img.url;
                    return url === scannedData.imageUrl;
                });
                if (!alreadyInImages) {
                    mergedImages = [{ url: scannedData.imageUrl, alt: 'Primary product image' }, ...mergedImages];
                }
            }
            // Remove duplicate images by normalized filename and size
            mergedImages = deduplicateImageObjects(mergedImages);
            setForm(prev => ({
                ...prev,
                ...scannedData,
                images: mergedImages,
                imageUrl: mergedImages[0]?.url || mergedImages[0] || '',
                productUrl: urlInput.trim()
            }));

            // After scan, generate description and visual notes from the extracted images
            const images = mergedImages;
            const primaryImage = images[0]?.url || images[0] || '';
            if (primaryImage || images.length > 0) {
                setGeneratingNotes(true);
                try {
                    const result = await generateVisualNotes(primaryImage, scannedData.name || '', images);
                    setForm(prev => ({
                        ...prev,
                        visualNotes: result.visualNotes || prev.visualNotes,
                        description: result.description || prev.description
                    }));
                } catch (genError) {
                    console.error('Error generating from scanned images:', genError);
                } finally {
                    setGeneratingNotes(false);
                }
            }
        } catch (error) {
            console.error('Error scanning URL:', error);
            setScanError('Failed to scan URL. You can enter product details manually.');
            setForm(prev => ({
                ...prev,
                productUrl: urlInput.trim()
            }));
        } finally {
            setScanning(false);
        }
    };

    const handleAddFeature = () => {
        if (!newFeature.trim()) return;

        setForm(prev => ({
            ...prev,
            features: [...(prev.features || []), newFeature.trim()]
        }));
        setNewFeature('');
    };

    const handleRemoveFeature = (index) => {
        setForm(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    const handleAddImage = () => {
        if (!newImageUrl.trim()) return;

        // Check if URL already exists
        const exists = form.images?.some(img => {
            const imgUrl = typeof img === 'string' ? img : img.url;
            return imgUrl === newImageUrl.trim();
        });

        if (!exists) {
            setForm(prev => ({
                ...prev,
                images: [...(prev.images || []), { url: newImageUrl.trim(), alt: '' }]
            }));
        }
        setNewImageUrl('');
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        const validFiles = files.filter(file => {
            if (!file.type.startsWith('image/')) return false;
            if (file.size > MAX_FILE_SIZE) {
                console.error(`File "${file.name}" exceeds 10MB limit`);
                return false;
            }
            return true;
        });
        if (validFiles.length === 0) return;

        setUploadingFiles(true);
        let processedCount = 0;

        validFiles.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const dataUrl = event.target.result;
                setForm(prev => ({
                    ...prev,
                    images: [
                        ...(prev.images || []),
                        { url: dataUrl, alt: file.name.replace(/\.[^/.]+$/, '') }
                    ]
                }));
                processedCount++;
                if (processedCount >= validFiles.length) {
                    setUploadingFiles(false);
                }
            };
            reader.onerror = () => {
                console.error('Error reading file:', file.name);
                processedCount++;
                if (processedCount >= validFiles.length) {
                    setUploadingFiles(false);
                }
            };
            reader.readAsDataURL(file);
        });

        e.target.value = '';
    };

    const hasAnyImages = form.images?.length > 0 && form.images.some(img => {
        const url = typeof img === 'string' ? img : img.url;
        return url && url.length > 0;
    });

    const handleGenerateFromImages = async () => {
        if (!hasAnyImages) return;

        setGeneratingNotes(true);
        try {
            const firstImageUrl = form.images?.[0]?.url || (typeof form.images?.[0] === 'string' ? form.images[0] : '');
            const result = await generateVisualNotes(firstImageUrl, form.name, form.images || []);
            setForm(prev => ({
                ...prev,
                visualNotes: result.visualNotes || prev.visualNotes,
                description: result.description || prev.description
            }));
        } catch (error) {
            console.error('Error generating from images:', error);
        } finally {
            setGeneratingNotes(false);
        }
    };

    const handleCropComplete = (croppedDataUrl) => {
        if (cropImageIndex !== null) {
            setForm(prev => ({
                ...prev,
                images: prev.images.map((img, i) =>
                    i === cropImageIndex ? { ...(typeof img === 'string' ? { url: img, alt: '' } : img), url: croppedDataUrl } : img
                )
            }));
        }
        setShowCropCanvas(false);
        setCropImageIndex(null);
    };

    const handleInpaintComplete = (maskBase64, prompt, mode) => {
        // For product images we just store the result; actual inpainting would require server call
        // For now, close the mask drawer - the mask tool is available for future integration
        setShowMaskDrawer(false);
        setMaskImageIndex(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Set imageUrl from first image for backward compatibility
        const firstUrl = form.images?.[0] ? getImageUrl(form.images[0]) : '';
        onSave({ ...form, imageUrl: firstUrl });
    };

    const isEditing = !!product?.id;

    return (
        <>
            {/* Backdrop */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 9998,
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease'
                }}
                onClick={handleAttemptClose}
            />
            {/* Panel */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    maxWidth: '560px',
                    background: 'var(--color-bg-primary)',
                    boxShadow: isOpen ? '-4px 0 24px rgba(0, 0, 0, 0.15)' : 'none',
                    zIndex: 9999,
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4) var(--space-5)',
                    borderBottom: '1px solid var(--color-border)',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <ShoppingBag size={20} style={{ color: 'var(--color-primary)' }} />
                        <h3 style={{ margin: 0 }}>
                            {isEditing ? 'Edit Product' : 'Add Product'}
                        </h3>
                    </div>
                    <button
                        onClick={handleAttemptClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-1)' }}
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-5)' }}>
                    {/* URL Scanner Section */}
                    <div style={{
                        padding: 'var(--space-4)',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)'
                        }}>
                            <Link size={14} style={{ marginRight: 'var(--space-1)', verticalAlign: 'middle' }} />
                            Product URL
                        </label>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <input
                                type="url"
                                value={urlInput}
                                onChange={(e) => setUrlInput(e.target.value)}
                                placeholder="https://example.com/products/item"
                                className="input"
                                style={{ flex: 1 }}
                            />
                            <button
                                type="button"
                                onClick={handleScanUrl}
                                disabled={!urlInput.trim() || scanning}
                                className="btn btn-secondary"
                            >
                                {scanning ? (
                                    <>
                                        <Loader2 size={14} className="spin" />
                                        Scanning...
                                    </>
                                ) : (
                                    <>
                                        <Search size={14} />
                                        Scan
                                    </>
                                )}
                            </button>
                        </div>
                        {scanError && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginTop: 'var(--space-2)',
                                padding: 'var(--space-2)',
                                background: 'var(--warning-50)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--warning-700)'
                            }}>
                                <AlertCircle size={14} />
                                {scanError}
                            </div>
                        )}
                        <p style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            margin: 'var(--space-2) 0 0 0'
                        }}>
                            Paste a product URL and click Scan to auto-fill product details.
                        </p>
                    </div>

                    {/* Product Details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                        {/* Name */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-1)'
                            }}>
                                Product Name *
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Premium Wireless Headphones"
                                className="input"
                                style={{ width: '100%' }}
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 'var(--space-1)'
                            }}>
                                <label style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500
                                }}>
                                    <FileText size={14} style={{ marginRight: 'var(--space-1)', verticalAlign: 'middle' }} />
                                    Description
                                </label>
                                <button
                                    type="button"
                                    onClick={handleGenerateFromImages}
                                    disabled={!hasAnyImages || generatingNotes}
                                    className="btn btn-ghost"
                                    style={{
                                        padding: 'var(--space-1) var(--space-2)',
                                        fontSize: 'var(--font-size-xs)',
                                        gap: 'var(--space-1)'
                                    }}
                                    title={!hasAnyImages ? 'Add product images first' : 'Generate description and visual notes from all product images'}
                                >
                                    {generatingNotes ? (
                                        <>
                                            <Loader2 size={12} className="spin" />
                                            Analyzing...
                                        </>
                                    ) : (
                                        <>
                                            <Wand2 size={12} />
                                            Generate from Images
                                        </>
                                    )}
                                </button>
                            </div>
                            <textarea
                                value={form.description}
                                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Brief product description..."
                                className="input"
                                rows={3}
                                style={{ resize: 'vertical', width: '100%' }}
                            />
                        </div>

                        {/* Price and Category */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    <DollarSign size={14} style={{ marginRight: 'var(--space-1)', verticalAlign: 'middle' }} />
                                    Price
                                </label>
                                <input
                                    type="text"
                                    value={form.price}
                                    onChange={(e) => setForm(prev => ({ ...prev, price: e.target.value }))}
                                    placeholder="e.g., $99.99"
                                    className="input"
                                />
                            </div>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    <Tag size={14} style={{ marginRight: 'var(--space-1)', verticalAlign: 'middle' }} />
                                    Category
                                </label>
                                <input
                                    type="text"
                                    value={form.category}
                                    onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                                    placeholder="e.g., Electronics"
                                    className="input"
                                />
                            </div>
                        </div>

                        {/* Product Images */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                <Image size={14} style={{ marginRight: 'var(--space-1)', verticalAlign: 'middle' }} />
                                Product Images
                                <span style={{
                                    fontWeight: 400,
                                    color: 'var(--color-text-tertiary)',
                                    marginLeft: 'var(--space-2)',
                                    fontSize: 'var(--font-size-xs)'
                                }}>
                                    (All images will be used as AI references)
                                </span>
                            </label>

                            {/* Image Gallery */}
                            {form.images?.length > 0 && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                    gap: 'var(--space-2)',
                                    marginBottom: 'var(--space-3)'
                                }}>
                                    {form.images.map((img, index) => {
                                        const imgUrl = getImageUrl(img);
                                        const imgAlt = typeof img === 'string' ? '' : (img.alt || '');
                                        return (
                                            <div
                                                key={index}
                                                className="product-image-tile"
                                                style={{
                                                    position: 'relative',
                                                    aspectRatio: '1',
                                                    borderRadius: 'var(--radius-md)',
                                                    border: '1px solid var(--color-border)',
                                                    overflow: 'hidden',
                                                    background: 'var(--color-bg-secondary)',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <img
                                                    src={imgUrl}
                                                    alt={imgAlt || `Product image ${index + 1}`}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    onClick={() => setFullscreenIndex(index)}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div style={{
                                                    display: 'none',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '100%',
                                                    height: '100%',
                                                    color: 'var(--color-text-tertiary)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    textAlign: 'center',
                                                    padding: 'var(--space-2)'
                                                }}>
                                                    Failed to load
                                                </div>
                                                {/* Hover overlay with action buttons */}
                                                <div
                                                    className="product-image-overlay"
                                                    style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background: 'rgba(0, 0, 0, 0.5)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '4px',
                                                        opacity: 0,
                                                        transition: 'opacity 0.15s ease'
                                                    }}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => setFullscreenIndex(index)}
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '50%',
                                                            background: 'rgba(255, 255, 255, 0.2)',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white',
                                                            padding: 0
                                                        }}
                                                        title="View fullscreen"
                                                    >
                                                        <Maximize2 size={13} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setCropImageIndex(index);
                                                            setShowCropCanvas(true);
                                                        }}
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '50%',
                                                            background: 'rgba(255, 255, 255, 0.2)',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white',
                                                            padding: 0
                                                        }}
                                                        title="Crop image"
                                                    >
                                                        <Crop size={13} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setMaskImageIndex(index);
                                                            setShowMaskDrawer(true);
                                                        }}
                                                        style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '50%',
                                                            background: 'rgba(255, 255, 255, 0.2)',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white',
                                                            padding: 0
                                                        }}
                                                        title="Edit / Remove objects"
                                                    >
                                                        <Paintbrush size={13} />
                                                    </button>
                                                </div>
                                                {/* Delete button (always visible) */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setForm(prev => ({
                                                            ...prev,
                                                            images: prev.images.filter((_, i) => i !== index)
                                                        }));
                                                    }}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '4px',
                                                        right: '4px',
                                                        width: '22px',
                                                        height: '22px',
                                                        borderRadius: '50%',
                                                        background: 'rgba(0, 0, 0, 0.6)',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        padding: 0,
                                                        zIndex: 1
                                                    }}
                                                    title="Remove image"
                                                >
                                                    <X size={14} />
                                                </button>
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '4px',
                                                    left: '4px',
                                                    padding: '2px 6px',
                                                    background: 'rgba(0, 0, 0, 0.6)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: '10px',
                                                    color: 'white'
                                                }}>
                                                    {index + 1}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Add New Image - URL or Upload */}
                            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                                <input
                                    type="url"
                                    value={newImageUrl}
                                    onChange={(e) => setNewImageUrl(e.target.value)}
                                    placeholder="https://example.com/image.jpg"
                                    className="input"
                                    style={{ flex: 1 }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddImage();
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddImage}
                                    disabled={!newImageUrl.trim()}
                                    className="btn btn-ghost"
                                    title="Add image URL"
                                >
                                    <Plus size={16} />
                                </button>
                                <div style={{
                                    width: '1px',
                                    height: '24px',
                                    background: 'var(--color-border)',
                                    flexShrink: 0
                                }} />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingFiles}
                                    className="btn btn-ghost"
                                    title="Upload image files"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)'
                                    }}
                                >
                                    {uploadingFiles ? (
                                        <Loader2 size={16} className="spin" />
                                    ) : (
                                        <Upload size={16} />
                                    )}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                            </div>
                            <p style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                margin: 'var(--space-2) 0 0 0'
                            }}>
                                Add images by URL or upload from your computer. Click an image to view fullscreen, or hover to crop and edit.
                            </p>
                        </div>

                        {/* Features */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                Key Features
                            </label>

                            {form.features?.length > 0 && (
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 'var(--space-2)',
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    {form.features.map((feature, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-1)',
                                                padding: 'var(--space-1) var(--space-2)',
                                                background: 'var(--color-bg-secondary)',
                                                borderRadius: 'var(--radius-full)',
                                                fontSize: 'var(--font-size-sm)'
                                            }}
                                        >
                                            <CheckCircle size={12} style={{ color: 'var(--success-500)' }} />
                                            {feature}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFeature(index)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '2px',
                                                    display: 'flex',
                                                    color: 'var(--color-text-tertiary)'
                                                }}
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <input
                                    type="text"
                                    value={newFeature}
                                    onChange={(e) => setNewFeature(e.target.value)}
                                    placeholder="Add a feature..."
                                    className="input"
                                    style={{ flex: 1 }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddFeature();
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddFeature}
                                    disabled={!newFeature.trim()}
                                    className="btn btn-ghost"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                        </div>

                        {/* AI Generation Settings */}
                        <div style={{
                            padding: 'var(--space-4)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-3)'
                            }}>
                                <Sparkles size={16} style={{ color: 'var(--color-primary)' }} />
                                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                                    AI Generation Settings
                                </span>
                            </div>
                            <div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    <label style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500
                                    }}>
                                        Visual Notes
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleGenerateFromImages}
                                        disabled={!hasAnyImages || generatingNotes}
                                        className="btn btn-ghost"
                                        style={{
                                            padding: 'var(--space-1) var(--space-2)',
                                            fontSize: 'var(--font-size-xs)',
                                            gap: 'var(--space-1)'
                                        }}
                                        title={!hasAnyImages ? 'Add product images first' : 'Generate visual notes from all product images'}
                                    >
                                        {generatingNotes ? (
                                            <>
                                                <Loader2 size={12} className="spin" />
                                                Analyzing...
                                            </>
                                        ) : (
                                            <>
                                                <Wand2 size={12} />
                                                Generate from Images
                                            </>
                                        )}
                                    </button>
                                </div>
                                <textarea
                                    value={form.visualNotes || ''}
                                    onChange={(e) => setForm(prev => ({ ...prev, visualNotes: e.target.value }))}
                                    placeholder="Describe the product's appearance for AI image generation...

Example: Blue glass bottle with matte silver cap. Minimalist white label featuring a green leaf logo. Approximately 8 inches tall, cylindrical shape."
                                    className="input"
                                    rows={5}
                                    style={{
                                        resize: 'vertical',
                                        width: '100%',
                                        minHeight: '120px'
                                    }}
                                />
                                <p style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    margin: 'var(--space-2) 0 0 0'
                                }}>
                                    Help the AI generate accurate images by describing: colors, shape, size, materials, packaging, logo placement, and distinctive visual features.
                                </p>
                            </div>
                        </div>

                        {/* Active Toggle */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)'
                        }}>
                            <div>
                                <span style={{ fontWeight: 500 }}>Include in Creatives</span>
                                <p style={{
                                    margin: 'var(--space-1) 0 0 0',
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Active products will be available for AI-generated content
                                </p>
                            </div>
                            <label style={{
                                position: 'relative',
                                display: 'inline-block',
                                width: '44px',
                                height: '24px'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                                    style={{
                                        opacity: 0,
                                        width: 0,
                                        height: 0
                                    }}
                                />
                                <span style={{
                                    position: 'absolute',
                                    cursor: 'pointer',
                                    inset: 0,
                                    background: form.isActive ? 'var(--success-500)' : 'var(--color-bg-tertiary)',
                                    borderRadius: 'var(--radius-full)',
                                    transition: 'background var(--transition-fast)'
                                }}>
                                    <span style={{
                                        position: 'absolute',
                                        content: '""',
                                        height: '18px',
                                        width: '18px',
                                        left: form.isActive ? '23px' : '3px',
                                        bottom: '3px',
                                        background: 'white',
                                        borderRadius: '50%',
                                        transition: 'left var(--transition-fast)',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                    }} />
                                </span>
                            </label>
                        </div>
                    </div>

                </div>

                    {/* Footer */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-4) var(--space-5)',
                        borderTop: '1px solid var(--color-border)',
                        flexShrink: 0
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-ghost"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading || !form.name.trim()}
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={16} className="spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <ShoppingBag size={16} />
                                    {isEditing ? 'Update Product' : 'Add Product'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Fullscreen Image Viewer */}
            {fullscreenIndex !== null && form.images?.[fullscreenIndex] && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        zIndex: 10001,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => setFullscreenIndex(null)}
                >
                    <button
                        onClick={(e) => { e.stopPropagation(); setFullscreenIndex(null); }}
                        style={{
                            position: 'absolute',
                            top: 'var(--space-6)',
                            right: 'var(--space-6)',
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '50%',
                            width: '48px',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            cursor: 'pointer'
                        }}
                        title="Close"
                    >
                        <X size={24} />
                    </button>

                    {/* Prev arrow */}
                    {form.images.length > 1 && fullscreenIndex > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setFullscreenIndex(fullscreenIndex - 1); }}
                            style={{
                                position: 'absolute',
                                left: 'var(--space-6)',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '50%',
                                width: '48px',
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            <ChevronLeft size={24} />
                        </button>
                    )}

                    {/* Next arrow */}
                    {form.images.length > 1 && fullscreenIndex < form.images.length - 1 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setFullscreenIndex(fullscreenIndex + 1); }}
                            style={{
                                position: 'absolute',
                                right: 'var(--space-6)',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '50%',
                                width: '48px',
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                cursor: 'pointer'
                            }}
                        >
                            <ChevronRight size={24} />
                        </button>
                    )}

                    <img
                        src={getImageUrl(form.images[fullscreenIndex])}
                        alt={`Product image ${fullscreenIndex + 1}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            cursor: 'default',
                            borderRadius: 'var(--radius-md)'
                        }}
                    />

                    {/* Image counter */}
                    {form.images.length > 1 && (
                        <div style={{
                            position: 'absolute',
                            bottom: 'var(--space-6)',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: 'var(--font-size-sm)',
                            background: 'rgba(0,0,0,0.5)',
                            padding: 'var(--space-1) var(--space-3)',
                            borderRadius: 'var(--radius-full)'
                        }}>
                            {fullscreenIndex + 1} / {form.images.length}
                        </div>
                    )}
                </div>
            )}

            {/* Crop Canvas */}
            {showCropCanvas && cropImageIndex !== null && form.images?.[cropImageIndex] && (
                <ImageCropCanvas
                    imageUrl={getImageUrl(form.images[cropImageIndex])}
                    onCropComplete={handleCropComplete}
                    onCancel={() => { setShowCropCanvas(false); setCropImageIndex(null); }}
                />
            )}

            {/* Mask Drawing Canvas (Inpaint) */}
            {showMaskDrawer && maskImageIndex !== null && form.images?.[maskImageIndex] && (
                <MaskDrawingCanvas
                    imageUrl={getImageUrl(form.images[maskImageIndex])}
                    onMaskComplete={handleInpaintComplete}
                    onCancel={() => { setShowMaskDrawer(false); setMaskImageIndex(null); }}
                />
            )}

            {/* Hover styles for image tiles */}
            <style>{`
                .product-image-tile:hover .product-image-overlay {
                    opacity: 1 !important;
                }
            `}</style>

            {/* Unsaved Changes Confirmation */}
            <ConfirmationModal
                isOpen={showUnsavedModal}
                onClose={() => setShowUnsavedModal(false)}
                onConfirm={handleSaveAndClose}
                onSecondaryAction={handleDiscardAndClose}
                title="Unsaved Changes"
                message="You have unsaved changes. Would you like to save them before closing?"
                confirmText="Save Changes"
                secondaryActionText="Discard Changes"
                secondaryVariant="danger"
                cancelText="Keep Editing"
                variant="primary"
            />
        </>
    );
};

export default ProductEditorModal;
